import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Col, Flex, Row, Space, Tag, Typography } from 'antd';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { Icon } from './icons.tsx';
import { getBuildInformation, getReadiness } from './system-api.ts';
import { RequireAuth, RequirePage, useAuth } from './auth.tsx';
import type { PageKey } from '@youkuai/contracts';
import { ProjectsPage } from './projects-page.tsx';
import { GlobalContractsPage, ProjectRelatedContractsPage } from './contracts-page.tsx';
import { CreateProjectPage } from './create-project-page.tsx';
import { CustomersPage } from './customers-page.tsx';
import { OutboxOperationsPage } from './outbox-operations-page.tsx';
import { ExportGrantsPage } from './export-grants-page.tsx';
import { BankAccountsPage } from './bank-accounts-page.tsx';
import { PayeePage } from './payee-page.tsx';
import { McpAccessPage } from './mcp-access-page.tsx';
import { McpAccessAdminPage } from './mcp-access-admin-page.tsx';
import { ServiceTemplatesPage } from './service-templates-page.tsx';
import { ServicePackagesPage } from './video-packages-page.tsx';
import { PersonalSettlementAdminPage, ProjectPublicationsPage, ProjectWorkExpensesPage } from './operations-v2-pages.tsx';
import { WorkspaceShell } from './workspace-shell.tsx';
import { DynamicAccessPage } from './dynamic-access-page.tsx';
import { ExternalInvitationsPage } from './external-invitations-page.tsx';
import { PeoplePage } from './people-page.tsx';
import { ProjectContentPage, ProjectDeliveriesPage, ProjectIssuesPage, ProjectOperationsPage,
  ProjectOverviewPage, ProjectSettingsPage, ProjectTeamPage } from './project-workspace-pages.tsx';
import { pageForWebPath } from './page-access.ts';
import { ManagementDashboardPage } from './management-dashboard-page.tsx';
import { PersonalOutputPage } from './personal-output-page.tsx';
import { ContentProgressPage } from './content-progress-page.tsx';

const { Paragraph, Text, Title } = Typography;

function HomePage() {
  const { state } = useAuth();
  const signedIn = state.kind === 'ready';
  const pages = new Set(signedIn ? state.session.pageAccess : []);
  if (signedIn) return <Navigate to={pages.has('WORKSPACE') ? '/workspace' : pages.has('PROJECTS') ? '/projects' :
    pages.has('MANAGEMENT_DASHBOARD') ? '/management-dashboard' :
    pages.has('CONTRACTS') ? '/contracts' : pages.has('SETTLEMENTS') ? '/operations/settlement' :
      pages.has('PEOPLE') ? '/people' : pages.has('CUSTOMERS') ? '/customers' :
        pages.has('SERVICE_TEMPLATES') ? '/service-templates' : pages.has('SYSTEM') ? '/system/accounts' : '/system/status'} replace />;
  const canManage = pages.has('CUSTOMERS') || pages.has('SERVICE_TEMPLATES');
  const canManageCustomer = false;
  return (
    <main className="page-shell workspace-home">
      <section className="home-masthead">
        <div>
          <Tag className="phase-tag">业务工作台</Tag>
          <Title level={1}>{signedIn ? '今天，从哪项工作开始？' : '优快业务运营系统'}</Title>
        </div>
        <Paragraph className="hero-copy">
          项目、合同、服务交付与结算资料集中在同一工作区。系统只展示当前账号有权查看和处理的业务。
        </Paragraph>
        <Space wrap>
          <Link to="/projects"><Button type="primary" size="large">进入项目工作台</Button></Link>
          {canManage && <Link to="/customers"><Button size="large">{canManageCustomer ? '维护客户档案' : '查看客户档案'}</Button></Link>}
          <Link to="/system/status"><Button size="large">查看系统状态</Button></Link>
        </Space>
      </section>

      <Row gutter={[18, 18]} className="home-action-grid">
        {([
          ['/projects', '项目与交付', '查看项目、服务包与当前交付进度。', '01'],
          ...(canManage ? [['/customers', '客户档案', canManageCustomer ? '维护客户主体、联系人和开票资料。' : '查看客户主体、联系人和开票资料。', '02']] : []),
          ...(canManage ? [['/service-templates', '服务模板', '在同一版本维护动态规格、任务与费用。', '03']] : []),
          ['/workspace', '我的工作台', '查看当前账号范围内的内容进度与个人产出。', canManage ? '04' : '02'],
        ] as string[][]).map(([to, title, copy, index]) => (
          <Col xs={24} md={8} key={title}>
            <Link to={to!} className="home-action-link"><Card variant="borderless" className="home-action-card">
              <span>{index}</span><Title level={4}>{title}</Title><Paragraph>{copy}</Paragraph><b>进入 <Icon name="arrowRight" size={14} /></b>
            </Card></Link>
          </Col>
        ))}
      </Row>
    </main>
  );
}

function SystemStatusPage() {
  const build = useQuery({ queryKey: ['build-information'], queryFn: getBuildInformation });
  const readiness = useQuery({ queryKey: ['readiness'], queryFn: getReadiness, refetchInterval: 15_000 });
  const loading = build.isPending || readiness.isPending;
  const failed = build.isError || readiness.isError;

  return (
    <main className="page-shell status-page">
      <Flex justify="space-between" align="center" gap={16} wrap>
        <div>
          <Text type="secondary">运行证据</Text>
          <Title level={2}>系统状态</Title>
        </div>
        <Link to="/">返回概览</Link>
      </Flex>

      {loading && <Alert type="info" showIcon title="正在核验 API 与构建身份…" />}
      {failed && (
        <Alert
          type="error"
          showIcon
          title="无法核验正式 API"
          description="请确认 API 已在 127.0.0.1:3000 启动。页面不会把断线状态显示为成功。"
        />
      )}
      {!loading && !failed && build.data && readiness.data && (
        <Row gutter={[18, 18]}>
          <Col xs={24} lg={12}>
            <Card title="构建身份" variant="borderless">
              <dl className="status-list">
                <div><dt>环境</dt><dd>{build.data.environment}</dd></div>
                <div><dt>构建 ID</dt><dd>{build.data.buildId}</dd></div>
                <div><dt>Git SHA</dt><dd>{build.data.gitSha}</dd></div>
              </dl>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="依赖就绪" variant="borderless">
              <Space orientation="vertical" size="middle">
                <Tag color={readiness.data.status === 'ok' ? 'success' : 'error'}>
                  {readiness.data.status === 'ok' ? '已就绪' : '未就绪'}
                </Tag>
                {readiness.data.dependencies.map((dependency) => (
                  <Flex key={dependency.name} gap={12} align="center">
                    <Text>{dependency.name}</Text>
                    <Tag color={dependency.status === 'ok' ? 'success' : 'error'}>
                      {dependency.status}
                    </Tag>
                  </Flex>
                ))}
              </Space>
            </Card>
          </Col>
        </Row>
      )}
    </main>
  );
}

export function App() {
  const { state } = useAuth();
  return (
    <WorkspaceShell>
      {state.kind !== 'ready' && <header className="public-header"><Link to="/" className="brand-mark">
        <img src="/brand/youkuai-project-mark-128.webp" alt="" width="128" height="128" decoding="async" />
        <strong>优快</strong></Link>
        <Text>业务运营系统</Text><Link to="/system/status">系统状态</Link></header>}
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/system/status" element={<SystemStatusPage />} />
          <Route path="/projects" element={<Protected page="PROJECTS"><ProjectsPage /></Protected>} />
          <Route path="/workspace" element={<Protected page="WORKSPACE"><ContentProgressPage /></Protected>} />
          <Route path="/workspace/output" element={<Protected page="WORKSPACE"><PersonalOutputPage /></Protected>} />
          <Route path="/management-dashboard" element={<Protected page="MANAGEMENT_DASHBOARD"><ManagementDashboardPage /></Protected>} />
          <Route path="/contracts" element={<Protected page="CONTRACTS"><GlobalContractsPage /></Protected>} />
          <Route path="/operations/settlement" element={<Protected page="SETTLEMENTS"><PersonalSettlementAdminPage /></Protected>} />
          <Route path="/me/payee" element={<Protected page="WORKSPACE"><PayeePage /></Protected>} />
          <Route path="/me/ai-access" element={<Protected page="WORKSPACE"><McpAccessPage /></Protected>} />
          <Route path="/operations/bank-accounts" element={<Protected page="SETTLEMENTS"><BankAccountsPage /></Protected>} />
          <Route path="/projects/new" element={<Protected page="PROJECTS"><CreateProjectPage /></Protected>} />
          <Route path="/projects/:projectId" element={<Protected page="PROJECTS"><ProjectOverviewPage /></Protected>} />
          <Route path="/projects/:projectId/content" element={<Protected page="PROJECT_CONTENT"><ProjectContentPage /></Protected>} />
          <Route path="/projects/:projectId/changes" element={<Protected page="PROJECT_CHANGES"><ProjectIssuesPage /></Protected>} />
          <Route path="/projects/:projectId/deliveries" element={<Protected page="PROJECT_DELIVERIES"><ProjectDeliveriesPage /></Protected>} />
          <Route path="/projects/:projectId/publications" element={<Protected page="PROJECT_PUBLICATIONS"><ProjectPublicationsPage /></Protected>} />
          <Route path="/projects/:projectId/work-expenses" element={<Protected page="PROJECT_WORK_EXPENSES"><ProjectWorkExpensesPage /></Protected>} />
          <Route path="/projects/:projectId/packages" element={<Protected page="PROJECT_PACKAGES"><ServicePackagesPage /></Protected>} />
          <Route path="/projects/:projectId/operations" element={<Protected page="PROJECT_FINANCE"><ProjectOperationsPage /></Protected>} />
          <Route path="/projects/:projectId/team" element={<Protected page="PROJECT_MEMBERS"><ProjectTeamPage /></Protected>} />
          <Route path="/projects/:projectId/settings" element={<Protected page="PROJECT_SETTINGS"><ProjectSettingsPage /></Protected>} />
          <Route path="/customers" element={<Protected page="CUSTOMERS"><CustomersPage /></Protected>} />
          <Route path="/service-templates" element={<Protected page="SERVICE_TEMPLATES"><ServiceTemplatesPage /></Protected>} />
          <Route path="/standards" element={<Navigate to="/service-templates" replace />} />
          <Route path="/people" element={<Protected page="PEOPLE"><PeoplePage /></Protected>} />
          <Route path="/operations/outbox" element={<Protected page="SYSTEM"><OutboxOperationsPage /></Protected>} />
          <Route path="/operations/export-grants" element={<Protected page="SYSTEM"><ExportGrantsPage /></Protected>} />
          <Route path="/system/accounts" element={<Protected page="SYSTEM"><DynamicAccessPage initialTab="accounts" /></Protected>} />
          <Route path="/system/external-invitations" element={<Protected page="SYSTEM"><ExternalInvitationsPage /></Protected>} />
          <Route path="/system/roles" element={<Protected page="SYSTEM"><DynamicAccessPage initialTab="roles" /></Protected>} />
          <Route path="/system/mcp-access" element={<Protected page="SYSTEM"><McpAccessAdminPage /></Protected>} />
          <Route path="/system/users" element={<Navigate to="/system/accounts" replace />} />
          <Route path="/projects/:projectId/contracts" element={<Protected page="PROJECT_CONTRACTS"><ProjectRelatedContractsPage /></Protected>} />
          <Route path="/auth/callback" element={<div className="auth-gate">正在完成工作账号登录…</div>} />
          <Route path="/auth/silent-callback" element={<div className="auth-gate">正在续期工作账号会话…</div>} />
        </Routes>
    </WorkspaceShell>
  );
}

function Protected({ page, children }: { page: PageKey; children: ReactNode }) {
  const location = useLocation();
  const routePage = pageForWebPath(location.pathname) ?? page;
  return <RequireAuth><RequirePage page={routePage}>{children}</RequirePage></RequireAuth>;
}
